import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import typography from 'assets/theme/base/typography';
import logo from 'assets/images/logoProdutos/alertai_icon_single_light.svg';
import { NavLink, useLocation } from 'react-router-dom';
import { AppContext } from '../../context/AppContextProvider';
import { IRouteProps } from '../../routes/routes';
import { getPermissions } from 'services/DefaultService';
import { fontColorDarkTheme } from 'styles/global.styles';
import logoExpanded from 'assets/images/logoProdutos/alertai_icon_light.svg';
import { ExitToApp } from '@mui/icons-material';
import { t } from 'i18next';

interface HeaderComponentProps {
  onMenuClick?: () => void;
  routes: IRouteProps[];
}

interface SidebarCollapsedItemValue {
  routeIndex: number;
  collapsed: boolean;
}

export const checkIfContainPermission = (route: any) => {
  return (
    (route.key !== 'management' && getPermissions() !== 'ALL') ||
    getPermissions() === 'ALL'
  );
};

const HeaderComponent: React.FC<HeaderComponentProps> = ({
  onMenuClick,
  routes,
}) => {
  const { size } = typography;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const { setIsOpenedSidebar, isOpenedSidebar, isRouteAllowed } =
    useContext(AppContext);
  const [collapsedItem, setCollapsedItem] = useState<
    SidebarCollapsedItemValue[]
  >([]);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);

  const renderIconCollapse = (route: IRouteProps, routeIndex: number) => {
    let icon = <></>;
    if (route.collapse) {
      if (collapsedItem[routeIndex]?.collapsed) {
        icon = (
          <KeyboardArrowUp
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          />
        );
      } else {
        icon = (
          <KeyboardArrowDown
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          />
        );
      }
    }
    return icon;
  };

  const handleCollapseMenuItem = (route: IRouteProps, routeIndex: number) => {
    if (route.collapse && route.collapse.length > 0)
      setCollapsedItem(
        collapsedItem.map((v, i) => {
          if (v.routeIndex === routeIndex)
            return { ...v, collapsed: !v.collapsed };
          return { ...v, collapsed: false };
        }),
      );
  };

  const onMouseLeave = () => {
    setCollapsedItem(collapsedItem.map((v) => ({ ...v, collapsed: false })));
    setIsOpenedSidebar(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY !== null) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchStartY - touchEndY > 50) {
        // Detecta deslizar para cima
        setIsSidebarOpen(false);
      }
    }
    setTouchStartY(null);
  };

  useEffect(() => {
    if (routes.length > 0) {
      const newCollapsedVal: SidebarCollapsedItemValue[] = [];
      routes.forEach((r, index) => {
        if (r.collapse && r.collapse?.length > 0) {
          let val = false;
          if (
            r.name &&
            pathname.substring(1, r.name?.length + 1).toLowerCase() ===
            r.name?.toLowerCase()
          ) {
            val = true;
          }
          newCollapsedVal.push({ collapsed: val, routeIndex: index });
        }
      });
      setCollapsedItem(newCollapsedVal);
    }
  }, [routes]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const checkIfContainPermission = (route: any) => {
    return (
      (route.key !== 'management' && getPermissions() !== 'ALL') ||
      getPermissions() === 'ALL'
    );
  };

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ backgroundColor: '#202940' }}
      p={2.0}
    >
      <Box>
        <img src={logo} alt="Logo" style={{ width: '17%' }} />
      </Box>
      <Box>
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            mx: 2,
            textTransform: 'capitalize',
          }}
        >
          {t(routes.find((route) => route.route === pathname)?.name ?? '')}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <IconButton onClick={toggleSidebar}>
          <MenuIcon sx={{ color: '#fff' }} />
        </IconButton>
        <Drawer
          anchor="top"
          open={isSidebarOpen}
          onClose={toggleSidebar}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: '#202940',
              color: '#fff',
              p: 3,
            },
          }}
        >
          <List>
            <Box
              sx={{
                height: 30,
                flexShrink: 0,
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img key="logo-expanded" height={30} src={logoExpanded} />
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Divider
                color={fontColorDarkTheme}
                sx={{ mt: 2, width: '50%' }}
              />
            </Box>
            {routes.map((route, routeIndex) => {
              return (
                route.protected && (
                  <Box
                    key={routeIndex}
                    sx={{
                      color: 'white',
                    }}
                  >
                    {route.name === 'Sair' && (
                      <Divider color={fontColorDarkTheme} sx={{ my: 2 }} />
                    )}
                    <>
                      {isRouteAllowed(route.permissionId) ? (
                        route.key == 'management' ? (
                          <></>
                        ) : (
                          checkIfContainPermission(route) && (
                            <NavLink
                              to={route.route ?? pathname}
                              style={{
                                textDecoration: 'none',
                                color: fontColorDarkTheme,
                              }}
                              onClick={(e) => {
                                if (route.isDisabled) return e.preventDefault();
                                handleCollapseMenuItem(route, routeIndex);
                                if (
                                  !route.collapse ||
                                  route.collapse.length === 0
                                ) {
                                  setIsSidebarOpen(false);
                                }
                              }}
                            >
                              <Button
                                sx={{
                                  color: fontColorDarkTheme,
                                  width: '100%',
                                  p: 1.5,
                                  px: 2,
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                }}
                                endIcon={
                                  isOpenedSidebar &&
                                  renderIconCollapse(route, routeIndex)
                                }
                                startIcon={route.icon}
                              >
                                <Typography
                                  sx={{
                                    mr: 'auto',
                                    ml: 0,
                                    fontSize: 15,
                                    whiteSpace: 'nowrap',
                                    transition:
                                      'visibility 0s, opacity 0.5s linear',
                                  }}
                                >
                                  {t(route.name ?? '')}
                                </Typography>
                              </Button>
                            </NavLink>
                          )
                        )
                      ) : (
                        <></>
                      )}
                      <Box style={{ transition: 'max-height 0.3s ease-out' }}>
                        {route.collapse &&
                          collapsedItem.find((v) => v.routeIndex == routeIndex)
                            ?.collapsed &&
                          route.collapse.map((collapse, ci) => (
                            <NavLink
                              key={ci}
                              to={collapse.route ?? pathname}
                              style={{
                                textDecoration: 'none',
                                color: fontColorDarkTheme,
                              }}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <Button
                                variant={
                                  collapse.route === pathname
                                    ? 'contained'
                                    : 'text'
                                }
                                sx={{
                                  color: fontColorDarkTheme,
                                  width: '100%',
                                  p: 1.5,
                                  px: 2,
                                  pl: 5,
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                }}
                                startIcon={collapse.icon}
                              >
                                <Typography
                                  sx={{ mr: 'auto', ml: 0, fontSize: 14 }}
                                >
                                  {t(collapse.name ?? '')}
                                </Typography>
                              </Button>
                            </NavLink>
                          ))}
                      </Box>
                    </>
                  </Box>
                )
              );
            })}
            <Box sx={{ flexShrink: 0 }}>
              <Divider color={fontColorDarkTheme} sx={{ my: 2 }} />
              <NavLink to="/app/logout" style={{ textDecoration: 'none' }}>
                <Button
                  variant="text"
                  sx={{
                    color: 'red',
                    width: '100%',
                    p: 1.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                  startIcon={<ExitToApp />}
                >
                  <Typography
                    sx={{
                      mr: 'auto',
                      ml: 0,
                      fontSize: 16,
                      whiteSpace: 'nowrap',
                      transition: 'visibility 0s, opacity 0.5s linear',
                    }}
                  >
                    Sair
                  </Typography>
                </Button>
              </NavLink>
            </Box>
          </List>
        </Drawer>
      </Box>
    </Box>
  );
};

export default HeaderComponent;
